import { cn } from 'cn';
import { Tooltip, TooltipProvider } from '@/components/ui/tooltip';
import { THEMES, useTheme } from '@/lib/theme';

/**
 * The 56px icon rail.
 *
 * While someone is reading, the 280px sidebar collapses to this so the reading
 * column keeps its measure — but Home, Browse, Ask, Library and Learn stay
 * reachable, which is the whole reason it collapses rather than disappearing.
 *
 * <p>Destinations that have no screen yet are rendered <b>disabled</b> rather
 * than as links that go nowhere. A dead link reads as a broken product; a
 * disabled control with a reason reads as one still being built — and the
 * difference matters more here than it would in a finished app.
 */

export type RailDestination =
  | 'home'
  | 'browse'
  | 'review'
  | 'chat'
  | 'glossary'
  | 'admin'
  | 'settings'
  | 'library';

/**
 * Which screen the rail is sitting on. The current item is rendered as a
 * marker rather than a link, so it must know what it is marking — the admin
 * shell uses the same rail as the reader.
 */
export type RailCurrent = 'read' | 'admin';

interface RailItem {
  id: RailDestination;
  label: string;
  /** Where it goes. Null until the screen exists. */
  href: string | null;
}

/**
 * The rail is the only navigation in the product, so every screen that
 * exists must be reachable from it. Eleven screens were built and none of
 * them were linked here — they could only be reached by typing a query
 * parameter, which means that for anyone opening the app they did not
 * exist at all.
 */
const ITEMS: RailItem[] = [
  { id: 'home', label: 'Home', href: '?screen=home' },
  { id: 'browse', label: 'Browse', href: '?screen=home&browse=1' },
  { id: 'chat', label: 'Ask AI', href: '?screen=chat' },
  { id: 'review', label: 'Review queue', href: '?screen=review' },
  { id: 'glossary', label: 'Glossary', href: '?screen=glossary' },
  { id: 'admin', label: 'Admin', href: '?screen=admin' },
  { id: 'settings', label: 'Settings', href: '?screen=settings' },
  // Browser-local, which is what the mock designs for: saved khutbahs and
  // reading history are ids in this browser, and the screen says so. It does
  // NOT need an account -- that was the reason this sat unlinked, and it was
  // the wrong reason.
  { id: 'library', label: 'My library', href: '?screen=library' },
];

function RailIcon({ id }: { id: RailDestination }) {
  const common = {
    viewBox: '0 0 24 24',
    width: 18,
    height: 18,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (id) {
    case 'home':
      return (
        <svg {...common}>
          <path d="M3 10.5 12 3l9 7.5M5.5 9.5V20h13V9.5M9.5 20v-6h5v6" />
        </svg>
      );
    case 'browse':
      return (
        <svg {...common}>
          <path d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4.5 4.5" />
        </svg>
      );
    case 'review':
      return (
        <svg {...common}>
          <path d="M5 4h14v16H5zM9 9h6M9 13h6M9 17h3" />
        </svg>
      );
    case 'chat':
      return (
        <svg {...common}>
          <path d="M20 14a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'glossary':
      return (
        <svg {...common}>
          <path d="M4 5h16M4 5v14h16V5M8 9h8M8 13h5" />
        </svg>
      );
    case 'admin':
      return (
        <svg {...common}>
          <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7.5 19l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.6 1.6 0 0 0 3 13.6H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.7 7.5l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.6 1.6 0 0 0 10 3V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.6 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0 1.1 2.7H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...common}>
          <path d="M4 7h10M18 7h2M4 12h2M10 12h10M4 17h8M16 17h4" />
          <circle cx="16" cy="7" r="2" />
          <circle cx="8" cy="12" r="2" />
          <circle cx="14" cy="17" r="2" />
        </svg>
      );
    case 'library':
      return (
        <svg {...common}>
          <path d="M5 4h4v16H5zM11 4h3v16h-3zM16.5 4.6l3 .6-2.4 15.2-3-.6z" />
        </svg>
      );
  }
}

const CURRENT: Record<RailCurrent, { label: string; path: string }> = {
  read: {
    label: 'Read',
    path: 'M12 6.5c-1.6-1.4-3.9-2-7-2v13c3.1 0 5.4.6 7 2 1.6-1.4 3.9-2 7-2v-13c-3.1 0-5.4.6-7 2zM12 6.5v13',
  },
  admin: {
    label: 'Admin',
    path: 'M12 16V5M7.5 9.5 12 5l4.5 4.5M5 19h14',
  },
};

function CurrentIcon({ current }: { current: RailCurrent }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={CURRENT[current].path} />
    </svg>
  );
}

export function IconRail({ current = 'read' }: { current?: RailCurrent } = {}) {
  const { theme, setTheme } = useTheme();
  // Cycles rather than offering five radio buttons: the rail is 56px wide and
  // the full switcher belongs on the Settings screen. The label always names
  // what comes next, so the control says what it does.
  const nextTheme = THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length] ?? 'system';

  return (
    // One provider for the whole rail: the 300ms delay is paid once, and
    // moving from one icon to the next shows its label immediately rather
    // than waiting again — which is the behaviour that makes an icon-only
    // navigation legible at all.
    <TooltipProvider>
    <nav
      aria-label="Almimbar"
      // Logical border, not `border-r`: in an RTL interface the rail sits on
      // the other side and a physical property would put the edge inside it.
      className={cn(
        'hidden w-14 shrink-0 flex-col items-center gap-1 border-e border-sidebar-border',
        'bg-sidebar py-3 md:flex',
      )}
    >
      {/* No mark here any more. The rail carried one at 24px while it was the
          only place the brand appeared; the app bar above now shows the full
          lockup, and two marks in the same top-left corner read as the logo
          being printed twice rather than as one identity. Home is still one
          click away — it is the house icon below. */}
      {/* Where we are, so it is the current item rather than one of the
          destinations. */}
      <Tooltip label={CURRENT[current].label} side="right">
        <span
          aria-current="page"
          className={cn(
            'flex size-10 items-center justify-center rounded-md',
            'bg-sidebar-accent text-sidebar-primary',
          )}
        >
          <CurrentIcon current={current} />
          <span className="sr-only">{CURRENT[current].label}</span>
        </span>
      </Tooltip>

      <span className="my-1 h-px w-6 bg-sidebar-border" aria-hidden />

      {ITEMS.map((item) =>
        item.href === null ? (
          // Disabled with a reason rather than a link that goes nowhere: a
          // dead link reads as a broken product, a disabled control that
          // explains itself reads as one still being built.
          <Tooltip
            key={item.id}
            label={`${item.label} — needs an account, which is not built yet`}
            side="right"
          >
            {/*
              A disabled <button> fires no pointer events, so a tooltip on the
              button itself would never open — the one control whose tooltip
              carries the only explanation of why it is dead. The span takes
              the hover; the button keeps the disabled semantics.
            */}
            <span className="flex">
              <button
                type="button"
                disabled
                aria-disabled
                className="flex size-10 cursor-not-allowed items-center justify-center rounded-md text-muted-foreground opacity-40"
              >
                <RailIcon id={item.id} />
                <span className="sr-only">{item.label} (needs an account)</span>
              </button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip key={item.id} label={item.label} side="right">
            <a
              href={item.href}
              className="flex size-10 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-primary"
            >
              <RailIcon id={item.id} />
              <span className="sr-only">{item.label}</span>
            </a>
          </Tooltip>
        ),
      )}

      {/* The sidebar's footer, per the design system. The account lives here
          once authentication exists; until then it is the theme. */}
      <div className="mt-auto flex flex-col items-center gap-1">
        <Tooltip label={`Theme: ${theme} — switch to ${nextTheme}`} side="right">
        <button
          type="button"
          onClick={() => setTheme(nextTheme)}
          className="flex size-10 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-primary"
        >
          <svg
            viewBox="0 0 24 24"
            width={18}
            height={18}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 3a9 9 0 1 0 9 9 7 7 0 0 1-9-9z" />
          </svg>
          <span className="sr-only">Theme: {theme}. Switch to {nextTheme}.</span>
        </button>
        </Tooltip>
      </div>
    </nav>
    </TooltipProvider>
  );
}
